/**
 * Durable per-clip media cache: small remuxed mp4s in the Cache API, played
 * back from blob URLs.
 *
 * Structure mirrors `framecache.ts` (memoized `caches.open`, module singleton,
 * in-flight dedupe), with three deliberate departures:
 *
 *  1. **All metadata lives in the cache key URL**, so the in-memory index is
 *     derived from `cache.keys()` and can never drift from what is actually
 *     stored. If the browser evicts an entry, it disappears from the index on
 *     the next init — the UI cannot claim a clip is cached when it isn't.
 *  2. **Blob URLs are refcounted and revoked.** framecache.ts:100-102 argues
 *     "GC reclaims evicted bitmaps"; that reasoning does NOT transfer. A
 *     `blob:` URL is a document-lifetime registration pinning its Blob until
 *     explicitly revoked, so leaking them leaks the whole cache into memory.
 *  3. **Lookup is by covering range, not exact match.** An entry serves any
 *     clip whose [in, out] it contains, so trimming a clip inward keeps hitting
 *     the existing entry instead of discarding a large cached range.
 */

const CACHE_NAME = 'fabled-clips-v1';
const KEY_ORIGIN = 'https://clipcache.invalid';
/** Default soft budget; eviction is LRU over cached entries. */
export const DEFAULT_BUDGET_BYTES = 4 * 1024 * 1024 * 1024;

export interface ClipCacheEntry {
  /** Cache API key (also the identity of this entry). */
  url: string;
  /** `${archive}/${item}/${part}/${resolution}/${format}` */
  sourceKey: string;
  /** Source-time of the file's t=0 (i.e. the remux startOffset). */
  start: number;
  /** Source-time of the file's end. */
  end: number;
  /**
   * The blob reaches end-of-media, so it covers any out-point beyond `end`
   * — there is no more media to fetch. Without this, a clip whose out-point
   * exceeds the real stream duration caches successfully and then fails every
   * coverage check, silently re-downloading each time.
   */
  hitEnd: boolean;
  bytes: number;
  /**
   * Monotonic use counter, NOT a timestamp. Date.now() has millisecond
   * resolution, and lookups happen every frame — ties would make LRU eviction
   * pick arbitrarily, including evicting the entry just used.
   */
  lastUsed: number;
}

export function sourceKeyOf(
  archive: string,
  item: string,
  part: number,
  resolution: number,
  format: string,
): string {
  return `${archive}/${item}/${part}/${resolution}/${format}`;
}

/** ms integers — keys derived from floats must not carry float noise. */
function ms(t: number): number {
  return Math.round(t * 1000);
}

function buildKey(
  sourceKey: string,
  start: number,
  end: number,
  bytes: number,
  hitEnd: boolean,
): string {
  return `${KEY_ORIGIN}/v1/${sourceKey}/${ms(start)}/${ms(end)}/${bytes}/${hitEnd ? 1 : 0}`;
}

function parseKey(url: string): ClipCacheEntry | null {
  if (!url.startsWith(`${KEY_ORIGIN}/v1/`)) return null;
  const parts = url.slice(`${KEY_ORIGIN}/v1/`.length).split('/');
  // archive/item/part/res/format/start/end/bytes[/hitEnd]
  // 8 parts = pre-hitEnd entry; treat as not-at-end rather than orphaning it.
  if (parts.length !== 8 && parts.length !== 9) return null;
  const [archive, item, part, res, format, start, end, bytes, eof] = parts;
  const n = (s: string) => {
    const v = Number(s);
    return Number.isFinite(v) ? v : null;
  };
  const s = n(start);
  const e = n(end);
  const b = n(bytes);
  if (s === null || e === null || b === null) return null;
  return {
    url,
    sourceKey: sourceKeyOf(archive, item, Number(part), Number(res), format),
    start: s / 1000,
    end: e / 1000,
    hitEnd: eof === '1',
    bytes: b,
    lastUsed: 0,
  };
}

interface Materialized {
  objectUrl: string;
  refs: number;
}

/** An un-memoized handle to the live store, for checking a held one is current. */
async function freshHandle(): Promise<Cache | null> {
  try {
    if (typeof caches === 'undefined') return null;
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

export class ClipCache {
  private store: Promise<Cache | null> | null = null;
  /** sourceKey → entries, rebuilt from cache.keys() at init. */
  private index = new Map<string, ClipCacheEntry[]>();
  private ready: Promise<void> | null = null;
  /** cache key → live blob URL + refcount. */
  private live = new Map<string, Materialized>();
  private inflight = new Map<string, Promise<string | null>>();
  /** Sources whose remux failed — don't retry every tick. */
  private failed = new Set<string>();
  private onChange: (() => void) | null = null;
  /** Monotonic recency counter — see ClipCacheEntry.lastUsed. */
  private tick = 0;

  setOnChange(fn: (() => void) | null): void {
    this.onChange = fn;
  }

  private openCache(): Promise<Cache | null> {
    if (this.store) return this.store;
    this.store = (async () => {
      try {
        if (typeof caches === 'undefined') return null;
        return await caches.open(CACHE_NAME);
      } catch {
        return null;
      }
    })();
    return this.store;
  }

  /**
   * Drop the memoized Cache handle and rebuild the index from whatever is
   * really there now.
   *
   * A handle obtained before the store was deleted keeps *appearing* to work:
   * reads miss and writes land somewhere nobody else can see. Left alone that
   * makes the UI claim clips are cached while nothing is being persisted — the
   * exact failure this cache is meant to rule out.
   */
  private async revalidate(): Promise<Cache | null> {
    this.store = null;
    const store = await this.openCache();
    const next = new Map<string, ClipCacheEntry[]>();
    if (store) {
      for (const req of await store.keys()) {
        const entry = parseKey(req.url);
        if (!entry) continue;
        entry.lastUsed = ++this.tick;
        const list = next.get(entry.sourceKey) ?? [];
        list.push(entry);
        next.set(entry.sourceKey, list);
      }
    }
    this.index = next;
    this.onChange?.();
    return store;
  }

  /**
   * Re-read the store and rebuild the index. Call when the page regains focus:
   * storage can be cleared in another tab, or evicted while we were in the
   * background, and nothing else would notice until the next write.
   */
  refresh(): Promise<unknown> {
    return this.revalidate();
  }

  /** Build the index from actual cache contents. Idempotent. */
  init(): Promise<void> {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      const store = await this.openCache();
      if (!store) return;
      const keys = await store.keys();
      const now = ++this.tick;
      for (const req of keys) {
        const entry = parseKey(req.url);
        if (!entry) continue;
        entry.lastUsed = now;
        const list = this.index.get(entry.sourceKey) ?? [];
        list.push(entry);
        this.index.set(entry.sourceKey, list);
      }
      this.onChange?.();
    })();
    return this.ready;
  }

  /**
   * Synchronous covering-range lookup. Returns the smallest entry that fully
   * contains [inSec, outSec], or null. Smallest so a tightly-cached clip is
   * preferred over an incidental larger range.
   */
  lookup(sourceKey: string, inSec: number, outSec: number): ClipCacheEntry | null {
    const list = this.index.get(sourceKey);
    if (!list) return null;
    let best: ClipCacheEntry | null = null;
    for (const e of list) {
      // small epsilon: keys are ms-quantized, clip bounds are floats
      if (e.start > inSec + 0.001) continue;
      // An at-end entry covers any out-point past its end — there is no more
      // media, so nothing further could ever be fetched.
      if (!(e.end >= outSec - 0.001 || e.hitEnd)) continue;
      if (!best || e.end - e.start < best.end - best.start) best = e;
    }
    if (best) best.lastUsed = ++this.tick;
    return best;
  }

  isFailed(sourceKey: string, inSec: number, outSec: number): boolean {
    return this.failed.has(`${sourceKey}/${ms(inSec)}/${ms(outSec)}`);
  }

  markFailed(sourceKey: string, inSec: number, outSec: number): void {
    this.failed.add(`${sourceKey}/${ms(inSec)}/${ms(outSec)}`);
  }

  clearFailed(): void {
    this.failed.clear();
  }

  async put(
    sourceKey: string,
    start: number,
    end: number,
    blob: Blob,
    hitEnd = false,
  ): Promise<ClipCacheEntry | null> {
    const store = await this.openCache();
    if (!store) return null;
    const url = buildKey(sourceKey, start, end, blob.size, hitEnd);
    const entry: ClipCacheEntry = {
      url,
      sourceKey,
      start,
      end,
      hitEnd,
      bytes: blob.size,
      lastUsed: ++this.tick,
    };
    const write = async (target: Cache) =>
      target.put(
        new Request(url),
        // A fresh Response per attempt: put() consumes the body. The Blob
        // itself is re-readable, so retrying is safe.
        new Response(blob, { headers: { 'content-type': 'video/mp4' } }),
      );
    // Deliberately NOT wrapped in try/catch: quota failure is routine for
    // megabyte entries, and swallowing it (as framecache.ts:173-176 does for
    // JPEGs) would report "cached" for something never persisted. Let it throw.
    await write(store);
    // Verify through a FRESH handle, not `store`: a stale handle reads back its
    // own write perfectly well even though nothing else can see it. Without
    // this we would mark the clip cached having persisted nothing.
    const fresh = await freshHandle();
    if (fresh && !(await fresh.match(url))) {
      await this.revalidate(); // adopt the live store, rebuild the index
      const live = await this.openCache();
      if (live) await write(live);
    }
    const list = this.index.get(sourceKey) ?? [];
    // Drop any entry this one fully covers — it is now redundant.
    const covers = (e: ClipCacheEntry) =>
      e.start >= start && (e.end <= end || (hitEnd && e.hitEnd));
    const superseded = list.filter(covers);
    const kept = list.filter((e) => !covers(e));
    kept.push(entry);
    this.index.set(sourceKey, kept);
    for (const s of superseded) await this.deleteEntry(s);
    this.onChange?.();
    return entry;
  }

  /** Blob URL if already materialized — safe to call every frame. Takes no reference. */
  peekUrl(entry: ClipCacheEntry): string | null {
    return this.live.get(entry.url)?.objectUrl ?? null;
  }

  /**
   * Synchronously take a reference to an ALREADY-materialized blob URL.
   *
   * Needed because a <video> element is created synchronously but must own its
   * own reference for its whole life: relying on someone else's reference means
   * the URL can be revoked while the element still has it as `src`, which fails
   * as net::ERR_FILE_NOT_FOUND rather than anything obviously cache-related.
   */
  retain(entry: ClipCacheEntry): string | null {
    const m = this.live.get(entry.url);
    if (!m) return null;
    m.refs++;
    return m.objectUrl;
  }

  /**
   * Materialize (once) and take a reference. Callers MUST pair with release()
   * or the underlying Blob is pinned for the document's lifetime.
   */
  async acquireUrl(entry: ClipCacheEntry): Promise<string | null> {
    const existing = this.live.get(entry.url);
    if (existing) {
      existing.refs++;
      return existing.objectUrl;
    }
    const pending = this.inflight.get(entry.url);
    if (pending) {
      const url = await pending;
      if (!url) return null;
      const m = this.live.get(entry.url);
      if (m) m.refs++;
      return url;
    }
    const p = (async () => {
      const held = await this.openCache();
      if (!held) return null;
      // Match against the LIVE handle, not the held one. A stale handle serves
      // back its own entries perfectly well, so checking it would keep a clip
      // looking cached all session and then have it vanish on reload.
      const live = (await freshHandle()) ?? held;
      let res = await live.match(entry.url);
      if (!res) {
        // Either the browser evicted this entry or our handle is stale. Rebuild
        // from what is actually there before concluding it is gone.
        const store = await this.revalidate();
        res = store ? await store.match(entry.url) : undefined;
      }
      if (!res) {
        // Genuinely gone — drop it from the index so the UI stops claiming
        // this clip is cached.
        this.forgetEntry(entry);
        return null;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      this.live.set(entry.url, { objectUrl, refs: 1 });
      return objectUrl;
    })()
      .catch(() => null)
      .finally(() => this.inflight.delete(entry.url));
    this.inflight.set(entry.url, p);
    return p;
  }

  releaseUrl(entry: ClipCacheEntry): void {
    const m = this.live.get(entry.url);
    if (!m) return;
    m.refs--;
    if (m.refs <= 0) {
      URL.revokeObjectURL(m.objectUrl);
      this.live.delete(entry.url);
    }
  }

  private forgetEntry(entry: ClipCacheEntry): void {
    const list = this.index.get(entry.sourceKey);
    if (!list) return;
    const next = list.filter((e) => e.url !== entry.url);
    if (next.length) this.index.set(entry.sourceKey, next);
    else this.index.delete(entry.sourceKey);
    this.onChange?.();
  }

  async deleteEntry(entry: ClipCacheEntry): Promise<void> {
    const store = await this.openCache();
    const m = this.live.get(entry.url);
    if (m) {
      URL.revokeObjectURL(m.objectUrl);
      this.live.delete(entry.url);
    }
    await store?.delete(entry.url);
    this.forgetEntry(entry);
  }

  entries(): ClipCacheEntry[] {
    return [...this.index.values()].flat();
  }

  usage(): { bytes: number; count: number } {
    let bytes = 0;
    let count = 0;
    for (const e of this.entries()) {
      bytes += e.bytes;
      count++;
    }
    return { bytes, count };
  }

  /** Evict least-recently-used entries until under budget. */
  async evictToBudget(budget = DEFAULT_BUDGET_BYTES): Promise<number> {
    let { bytes } = this.usage();
    if (bytes <= budget) return 0;
    const byAge = this.entries().sort((a, b) => a.lastUsed - b.lastUsed);
    let evicted = 0;
    for (const e of byAge) {
      if (bytes <= budget) break;
      // Never evict something currently being played from.
      if ((this.live.get(e.url)?.refs ?? 0) > 0) continue;
      await this.deleteEntry(e);
      bytes -= e.bytes;
      evicted++;
    }
    return evicted;
  }

  async clear(): Promise<void> {
    for (const [, m] of this.live) URL.revokeObjectURL(m.objectUrl);
    this.live.clear();
    this.index.clear();
    this.failed.clear();
    try {
      if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME);
    } catch {
      /* ignore */
    }
    this.store = null;
    this.ready = null;
    this.onChange?.();
  }

  /** Ask the browser not to evict our origin storage under pressure. */
  async requestPersist(): Promise<boolean> {
    try {
      if (!navigator.storage?.persist) return false;
      if (await navigator.storage.persisted()) return true;
      return await navigator.storage.persist();
    } catch {
      return false;
    }
  }

  async estimate(): Promise<{ usage: number; quota: number } | null> {
    try {
      if (!navigator.storage?.estimate) return null;
      const e = await navigator.storage.estimate();
      return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
    } catch {
      return null;
    }
  }
}

let singleton: ClipCache | null = null;

/** Process-wide clip cache (shared by every preview engine instance). */
export function getClipCache(): ClipCache {
  if (!singleton) singleton = new ClipCache();
  return singleton;
}

/** Test hook. */
export function __resetClipCache(): void {
  singleton = null;
}
